import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { QuestionType } from '../../../enums/ai-quote-chat.enum';
import { ResponseOption, SimpleQuoteFormRequest } from '../../../models/ai-quote-chat.models';

@Component({
  selector: 'app-ai-quote-simple-form',
  templateUrl: './ai-quote-simple-form.component.html',
  styleUrl: './ai-quote-simple-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AiQuoteSimpleFormComponent implements OnInit, OnChanges {
  private _quoteFormRequest!: SimpleQuoteFormRequest;

  @Input()
  set quoteFormRequest(value: SimpleQuoteFormRequest) {
    this._quoteFormRequest = value;
    this.normalizeOptions();
    if (this.quoteForm) {
      this.buildForm();
    }
  }

  get quoteFormRequest(): SimpleQuoteFormRequest {
    return this._quoteFormRequest;
  }

  @Input() isMobileApp = false;

  private _isReadOnly = false;
  @Input()
  set isReadOnly(value: boolean) {
    const nextValue = !!value;
    const enteringReadOnly = nextValue && !this._isReadOnly;
    this._isReadOnly = nextValue;

    if (enteringReadOnly && !this.hasSubmittedSelection) {
      this.resetSelections();
    }
  }

  get isReadOnly(): boolean {
    return this._isReadOnly;
  }

  @Output() formSubmit = new EventEmitter<string>();

  quoteForm!: FormGroup;
  normalizedOptions: ResponseOption[] = [];
  selectedValue: string | null = null;
  selectedValues: string[] = [];
  isNotRequiredSelected = false;
  private hasSubmittedSelection = false;

  readonly questionType = QuestionType;

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.quoteForm = this.fb.group({
      value: [null, []]
    });
    this.normalizeOptions();
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteFormRequest'] && this.quoteForm) {
      this.normalizeOptions();
      this.buildForm();
    }
  }

  private buildForm(): void {
    if (!this.quoteForm) {
      return;
    }

    this.resetSelections();
  }

  private resetSelections(): void {
    this.selectedValue = null;
    this.selectedValues = [];
    this.isNotRequiredSelected = false;
    this.hasSubmittedSelection = false;

    if (this.quoteForm) {
      this.quoteForm.setControl('value', this.fb.control(null, []));
    }
  }

  private normalizeOptions(): void {
    const options = this.quoteFormRequest?.options ?? [];

    if (this.quoteFormRequest?.questionType === QuestionType.BOOLEAN && (!options || options.length === 0)) {
      this.normalizedOptions = [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ];
      return;
    }

    this.normalizedOptions = options.map((option: any) => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }

      return {
        label: option.label ?? option.value ?? '',
        value: option.value ?? option.label ?? ''
      };
    }).filter(option => !!option.value);
  }

  isSelected(value: string): boolean {
    if (this.quoteFormRequest?.questionType === QuestionType.MULTI_SELECT) {
      return this.selectedValues.includes(value);
    }

    return this.selectedValue === value;
  }

  // Determines if there is a selection made in read-only mode.
  hasReadOnlySelection(): boolean {
    return this.quoteFormRequest?.questionType === QuestionType.MULTI_SELECT
      ? this.selectedValues.length > 0
      : !!this.selectedValue;
  }

  // Determines if the read-only options should be displayed.
  shouldShowReadOnlyOptions(): boolean {
    return !this.hasReadOnlySelection() && !this.isNotRequiredSelected;
  }

  toggleSelection(value: string, checked: boolean): void {
    this.hasSubmittedSelection = false;
    this.isNotRequiredSelected = false;
    if (checked) {
      if (!this.selectedValues.includes(value)) {
        this.selectedValues = [...this.selectedValues, value];
      }
    } else {
      this.selectedValues = this.selectedValues.filter(item => item !== value);
    }
  }

  onSelectionChange(value: string): void {
    this.hasSubmittedSelection = false;
    this.isNotRequiredSelected = false;
    this.selectedValue = this.selectedValue === value ? null : value;
    this.quoteForm.patchValue({ value: this.selectedValue });
  }

  onSubmit(): void {
    this.quoteForm.markAllAsTouched();

    if (this.quoteForm.invalid) {
      return;
    }

    const payload = this.quoteFormRequest?.questionType === QuestionType.MULTI_SELECT
      ? this.selectedValues.map(value => `${value}=Yes`).join(',')
      : String(this.selectedValue ?? '');
    this.hasSubmittedSelection = true;
    this.isNotRequiredSelected = false;
    this.formSubmit.emit(payload);
    this.isReadOnly = true;
  }

  cancel(): void {
    this.resetSelections();
    this.isReadOnly = true;
    if (this.quoteFormRequest?.questionType === QuestionType.MULTI_SELECT) {
      this.isNotRequiredSelected = true;
      const availableValues = this.normalizedOptions.map(option => option.value);
      const responsetext = availableValues.map(value => `${value}=No`).join(',');
      this.formSubmit.emit(responsetext);
    } else {
      this.isNotRequiredSelected = false;
      this.formSubmit.emit('Not Required');
    }
  }
}
